import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import useMatrixClient from '../../hooks/useMatrixClient';
import { matrixUserService } from '../messaging/matrixUserService';
import { useMessagingStore } from '../../state/messagingStore';
import { useMatrixStore } from '../../state/matrixStore';
import useOtherUsersStore from '../../state/otherUsersStore';
import { useConversationStore } from '../../state/conversationStore';
import { useDmUiStore } from '../../state/dmUiStore';
import useTeamStore from '../../state/teamStore';
import { useProfileStore } from '../../state/profileStore';
import { useModalStore } from '../../state/modalStore';

// Extracted components
import VerticalDragDivider from './VerticalDragDivider';
import ChatList from './ChatList';
import TeamInvitePicker from './TeamInvitePicker';
import TeamInviteStatusModal from './TeamInviteStatusModal';
import InviteFlowOutgoing from './InviteFlowOutgoing';
import InviteFlowIncoming from './InviteFlowIncoming';
import MessageComposer from './MessageComposer';
import MessageBubble from './MessageBubble';
import PresenceAvatar from '../shared/PresenceAvatar';

// Extracted helpers
import {
  initialsOf,
  formatFullDateTime,
  calculateCooldownDaysRemaining,
  extractUidFromMxid,
  isAdminMxid,
  isRealUserMxid,
} from './chatHelpers';

const DEFAULT_PAGINATION = { hasMore: true, isLoading: false };
const API_URL = process.env.REACT_APP_API_URL || '';

// ── Discover People panel — shown when no conversation is selected ──
function DiscoverPeoplePanel() {
  const profile = useProfileStore((s) => s.profile);
  const openCandidate = useModalStore((s) => s.openCandidate);
  const openWithCandidate = useMatrixStore((s) => s.openWithCandidate);
  const setSelectedThread = useMessagingStore((s) => s.setSelectedThread);
  const createLocalInviteThread = useMessagingStore((s) => s.createLocalInviteThread);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(15);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const fetchUsers = useCallback(async () => {
    if (!profile?.u_id) return;
    const lat = profile.b_lat || profile.lat;
    const lng = profile.b_lng || profile.lng;
    if (!lat || !lng) return;

    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/profile/teams/local-users?focus=ALL&lat=${lat}&lng=${lng}&radius=100`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (Array.isArray(data)) setUsers(data);
    } catch (err) {
      console.error('[DiscoverPeople] fetch error:', err);
    }
    setLoading(false);
  }, [profile]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const myFocus = (profile?.u_customer_focus || '').toUpperCase();
  const myUid = Number(profile?.u_id);

  // Sort: same customer focus first, then others — both by distance
  const filtered = (() => {
    let list = users;
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(u => {
        const name = `${u.u_first_name || ''} ${u.u_last_name || ''}`.toLowerCase();
        const biz = (u.b_name || '').toLowerCase();
        const ind = (u.b_industry || '').toLowerCase();
        return name.includes(q) || biz.includes(q) || ind.includes(q);
      });
    }
    return [...list].sort((a, b) => {
      const aMatch = (a.u_customer_focus || '').toUpperCase() === myFocus ? 0 : 1;
      const bMatch = (b.u_customer_focus || '').toUpperCase() === myFocus ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return (a.distance_mi || 999) - (b.distance_mi || 999);
    });
  })();

  // Reset visible count when search changes
  useEffect(() => { setVisibleCount(15); }, [query]);

  // Infinite scroll: load 15 more when sentinel is visible
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || loadingMore || visibleCount >= filtered.length) return;
        setLoadingMore(true);
        setTimeout(() => {
          setVisibleCount((n) => Math.min(n + 15, filtered.length));
          setLoadingMore(false);
        }, 200);
      },
      { root: scrollContainerRef.current, threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [filtered.length, visibleCount, loadingMore]);

  const visibleUsers = filtered.slice(0, visibleCount);

  const handleStartChat = async (user) => {
    const candidateId = Number(user.u_id);
    if (!Number.isFinite(myUid) || !Number.isFinite(candidateId)) return;

    const a = Math.min(myUid, candidateId);
    const b = Math.max(myUid, candidateId);
    const aliasFull = `#eh.dm.u${a}_u${b}:ehandshake.com`;
    const displayName = [user.u_first_name, user.u_last_name].filter(Boolean).join(' ') || 'User';
    const profileImg = user.u_profile_picture || '/images/default-avatar.png';
    const businessName = user.b_name || '';

    // Load fresh conversations to check for existing active DM
    try { await useConversationStore.getState().loadConversations(); } catch {}

    // Check for existing DM first (active Matrix DM or accepted dm_relationship)
    const convState = useConversationStore.getState();
    const existingActiveDM = (convState.conversations || []).find((c) => {
      if (c.conversation_type !== 'active_matrix_dm' && c.conversation_type !== 'accepted_dm') return false;
      return Number(c.other_user_id) === Number(candidateId);
    });

    if (existingActiveDM) {
      // Already have an active chat — just focus on it
      const threadRef = existingActiveDM.room_id || aliasFull;
      setSelectedThread(threadRef);
      if (existingActiveDM.room_id) {
        useMatrixStore.getState().setSelectedRoomId(existingActiveDM.room_id);
      }
      return;
    }

    // Check for existing optimistic/pending thread
    const msgState = useMessagingStore.getState();
    const existingThread = (msgState.optimisticThreads || []).find((t) =>
      String(t.alias || '') === aliasFull || Number(t.peerUid) === candidateId
    );
    if (!existingThread) {
      createLocalInviteThread({ candidateId, candidateName: displayName, candidateAvatar: profileImg, candidateBusiness: businessName });
    }

    setSelectedThread(aliasFull);
    openWithCandidate({ candidateId, candidateName: displayName, candidateAvatar: profileImg, candidateBusiness: businessName });
  };

  const handleViewProfile = (user) => {
    openCandidate({ candidate: user, viewerUId: myUid, top: 90, right: 30, offsetRight: 0 });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Discover People Nearby</h3>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, business, or industry…"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00aef7]/30 focus:border-[#00aef7]"
        />
      </div>

      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#c4c4c4 transparent' }}>
        {loading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
                <div className="w-11 h-11 rounded-full bg-gray-200" />
                <div className="flex-1"><div className="h-4 bg-gray-200 rounded w-1/2 mb-1.5" /><div className="h-3 bg-gray-100 rounded w-3/4" /></div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-400">
            {query.trim() ? 'No matches found.' : 'No nearby professionals found.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {visibleUsers.map(user => {
              const name = [user.u_first_name, user.u_last_name].filter(Boolean).join(' ') || 'Unknown';
              const dist = user.distance_mi != null ? `${Math.round(user.distance_mi)} mi` : '';
              const photo = user.u_profile_picture || null;
              const initials = [user.u_first_name?.[0], user.u_last_name?.[0]].filter(Boolean).join('').toUpperCase();
              const userFocus = (user.u_customer_focus || '').toUpperCase();
              const focusMatch = userFocus === myFocus;

              return (
                <div
                  key={user.u_id}
                  onClick={() => handleViewProfile(user)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-[#00aef7]/5 transition-colors cursor-pointer"
                  title={`View ${name}'s profile`}
                >
                  {photo ? (
                    <img src={photo} alt="" className="w-11 h-11 rounded-full object-cover border-2 border-gray-200 hover:border-[#00aef7] transition-colors shrink-0" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#00aef7] to-[#0095d0] flex items-center justify-center text-white text-sm font-bold shrink-0">
                      {initials || '?'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-gray-800 truncate hover:text-[#00aef7] transition-colors">{name}</span>
                      {userFocus && (
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${
                          focusMatch ? 'bg-[#00aef7]/10 text-[#00aef7] border border-[#00aef7]/20' : 'bg-gray-100 text-gray-500 border border-gray-200'
                        }`}>{userFocus}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{user.b_name || 'No business'}</div>
                    <div className="text-[11px] text-gray-400 truncate">{[user.b_industry, dist].filter(Boolean).join(' · ')}</div>
                  </div>
                </div>
              );
            })}
            {/* Sentinel for infinite scroll */}
            {visibleCount < filtered.length && (
              <div ref={sentinelRef} className="flex items-center justify-center py-3">
                {loadingMore ? (
                  <div className="w-5 h-5 border-2 border-gray-200 border-t-[#00aef7] rounded-full animate-spin" />
                ) : (
                  <span className="text-[11px] text-gray-300">Loading more…</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <div className="shrink-0 px-4 py-2 border-t border-gray-100 bg-gray-50">
          <span className="text-[11px] text-gray-400">
            {filtered.length} professional{filtered.length !== 1 ? 's' : ''} within 100 miles
            {query.trim() ? ` matching "${query}"` : ''}
          </span>
        </div>
      )}
    </div>
  );
}

function ChatWorld() {
  const sidebarWidth = useDmUiStore((s) => s.sidebarWidth);
  const selectedThread = useMessagingStore((s) => s.selectedThread);
  const optimisticThreads = useMessagingStore((s) => s.optimisticThreads);
  const archivedThreads = useMessagingStore(s => s.archivedThreads || []);
  const conversations = useConversationStore(s => s.conversations);
  const setInviteDraft = useTeamStore((s) => s.setInviteDraft);
  const inviteDraft = useTeamStore((s) => s.inviteDraft);
  const openTeam = useModalStore((s) => s.openTeam);

  const openCandidate = useModalStore((s) => s.openCandidate);
  const getOtherUser = useOtherUsersStore((s) => s.getUser);
  const fetchOtherUsersByIds = useOtherUsersStore((s) => s.fetchByIds);

  const myTeams = useTeamStore((s) => s.myTeams || []);

// Captain gate (persists via eh_team_v1)
// IMPORTANT: do not rely only on role === 'captain' (some teams are “owned” via owner_u_id / captain_u_id)
// IMPORTANT: gating must not depend on inviteDraft; it must only depend on real team ownership/captainship.
const viewerUId = useProfileStore((s) => Number(s?.profile?.u_id));

const captainTeams = useMemo(() => {
  const uid = Number(viewerUId);
  if (!Number.isFinite(uid)) return [];

  return (myTeams || []).filter((t) => {
    const role = String(t?.role || '').toLowerCase();
    const ownerId = Number(t?.owner_u_id || 0);
    const captainId = Number(t?.captain_u_id || 0);

    return role === 'captain' || ownerId === uid || captainId === uid;
  });
}, [myTeams, viewerUId]);

const openCandidateForUid = useCallback(
  async (uId) => {
    const targetId = Number(uId);
    if (!Number.isFinite(targetId) || targetId <= 0) return;

    try {
      // Fetch full user card directly for DM context
      const res = await fetch(
        `/api/profile/dm/user-card/${targetId}`,
        { credentials: 'include' }
      );

      if (!res.ok) {
        console.error('[ChatWorld] Failed to fetch DM user card:', res.status);
        return;
      }

      const dmUserCard = await res.json();

      // Open modal with the complete data
      openCandidate({
        candidate: dmUserCard,
        viewerUId,
        top: 90,
        right: 30,
        offsetRight: 0,
      });
    } catch (err) {
      console.error('[ChatWorld] Error fetching DM user card:', err);
    }
  },
  [openCandidate, viewerUId]
);


const canInviteToTeam = Array.isArray(captainTeams) && captainTeams.length > 0;


  // Matrix-backed room state (selected Matrix room + messages)
  const selectedRoomId = useMatrixStore((s) => s.selectedRoomId);
  const getRoom = useMatrixStore((s) => s.getRoom);
  const addMessage = useMatrixStore((s) => s.addMessage);
  const typingUsers = useMatrixStore((s) => s.typingUsers);
  const remoteReadReceipts = useMatrixStore((s) => s.remoteReadReceipts);
  const allReactions = useMatrixStore((s) => s.reactions);

  // Simple render counter so we can see if something is looping
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  // Live Matrix client/service for sending messages
  const { client: mxClient, service: mxService } = useMatrixClient();

  // Local composer text for the active Matrix DM
  const [composerText, setComposerText] = useState('');
  const typingTimeoutRef = useRef(null);

  // Messaging store actions mirrored from ChatView.jsx
  const setSelectedThread = useMessagingStore((s) => s.setSelectedThread);
  const removeOptimisticDM = useMessagingStore((s) => s.removeOptimisticDM);
  const removePendingThread = useMessagingStore((s) => s.removePendingThread);
  const markInviteSent = useMessagingStore((s) => s.markInviteSent);
  const archiveThread = useMessagingStore((s) => s.archiveThread);

  // Local UI state for the outgoing invite message + sending state + retry mode
  const [inviteMessage, setInviteMessage] = useState('');
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [retryMode, setRetryMode] = useState(false);
  const [suppressDeclinedView, setSuppressDeclinedView] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // Reply state: the message object we're replying to (or null)
  const [replyingTo, setReplyingTo] = useState(null);

  // Edit state: the message object we're editing (or null)
  const [editingMessage, setEditingMessage] = useState(null);

  // Delete confirmation state: the message object pending deletion (or null)
  const [deletingMessage, setDeletingMessage] = useState(null);

  // Scroll-to-bottom button: visible when user has scrolled up
  const [showScrollDown, setShowScrollDown] = useState(false);

  // Local UI state for the incoming invite flow (invitee)
  const [declineNote, setDeclineNote] = useState('');
  const [isAcceptingInvite, setIsAcceptingInvite] = useState(false);
  const [hasAcceptedInvite, setHasAcceptedInvite] = useState(false);
  const [acceptedPeerIdentity, setAcceptedPeerIdentity] = useState(null);

const [isTeamPickerOpen, setIsTeamPickerOpen] = useState(false);
const [pickedTeamId, setPickedTeamId] = useState(null);

// Team invite terminal-status modal (replaces browser alert)
const [teamInviteStatusModal, setTeamInviteStatusModal] = useState({
  open: false,
  message: '',
});

// Self-block modal: shown when the user tries to invite someone they blocked
const [selfBlockModal, setSelfBlockModal] = useState({
  open: false,
  blockedAt: null,
  otherUserId: null,
  otherUserName: '',
});

  const [inviteReadyToSend, setInviteReadyToSend] = useState(false);
  const [isInviteReadyToastOpen, setIsInviteReadyToastOpen] = useState(false);
  const closeTeamPicker = () => {
    setIsTeamPickerOpen(false);
    setPickedTeamId(null);
    setIsInviteReadyToastOpen(false);

    // Canceling team selection must also clear the chosen team_id
    // so the "Send invite" header button cannot remain visible.
    if (inviteDraft) {
      setInviteDraft({
        ...(inviteDraft || {}),
        team_id: null,
      });
    }
  };
  // Selected thread for invite flows:
  // 1) Look in active optimisticThreads by alias
  // 2) If not found, fall back to archivedThreads by alias
  const thread = useMemo(() => {
    if (!selectedThread) return null;
    const selStr = String(selectedThread);

    // First: active optimistic threads
    if (optimisticThreads && optimisticThreads.length) {
      const activeHit = optimisticThreads.find(
        (th) => th && String(th.alias || '') === selStr
      );
      if (activeHit) return activeHit;
    }

    // Fallback: archived threads (declined invites, cooldown, etc.)
    if (archivedThreads && archivedThreads.length) {
      const archivedHit = archivedThreads.find(
        (th) => th && String(th.alias || '') === selStr
      );
      if (archivedHit) return archivedHit;
    }

    return null;
  }, [selectedThread, optimisticThreads, archivedThreads]);

useEffect(() => {
  if (!thread) return;   // <--- This stops the infinite loop


  setSuppressDeclinedView(false);
}, [thread]);


  useEffect(() => {
    if (!suppressDeclinedView) return;



    const timer = setTimeout(() => {

      setSuppressDeclinedView(false);
    }, 7000); // keep decline view suppressed for ~7 seconds after a retry send

    return () => {

      clearTimeout(timer);
    };
  }, [suppressDeclinedView]);



  // When a real Matrix room is selected after acceptance, clear the "connecting" flag
  useEffect(() => {
    if (selectedRoomId && hasAcceptedInvite) {

      setHasAcceptedInvite(false);
    }
  }, [selectedRoomId, hasAcceptedInvite]);


  // Safety: if we're looking at an invite thread, do not keep any Matrix room selected.
  // This prevents the split-screen case where a new invite is shown on top while an
  // old accepted DM room is still active underneath.
  useEffect(() => {
    if (!thread) return;
    if (!thread.direction) return;             // new guard
    if (!selectedRoomId) return;               // prevents unnecessary resets

    if (thread.direction === 'outgoing' || thread.direction === 'incoming') {
      setSelectedRoomId(null);
    }
  }, [thread, selectedRoomId]);




  // Are we in the outgoing inviter flow for this optimistic thread?
  const isOutgoingInvite = useMemo(() => {
    if (!thread) return false;
    return thread.direction === 'outgoing';
  }, [thread]);

  // Are we in the incoming invitee flow for this optimistic thread?
  const isIncomingInvite = useMemo(() => {
    if (!thread) return false;
    return thread.direction === 'incoming';
  }, [thread]);


  // Invite-sent flag with retry override (same semantics as ChatView)
  const inviteSent = useMemo(() => {
    if (!thread) return false;
    if (retryMode) return false;
    return !!thread.inviteSent;
  }, [thread, retryMode]);

  const headerIdentity = useMemo(() => {
    if (!thread) return null;
    const peerUidNum =
      thread.peerUid != null && thread.peerUid !== ''
        ? Number(thread.peerUid)
        : null;

    const titleFromThread =
      thread.displayName && String(thread.displayName).trim().length > 0
        ? thread.displayName
        : peerUidNum
        ? `User ${peerUidNum}`
        : 'Chat';

    return {
      peerUid: peerUidNum,
      title: titleFromThread,
      avatarUrl: thread.avatarUrl || null,
    };
  }, [thread]);

  const headerTitle = headerIdentity?.title || 'Chat';
  const headerAvatar = headerIdentity?.avatarUrl || null;

  // --- Outgoing inviter declined helpers ------------------------------------
  // Look up the matching conversation row for this thread so we can
  // see the real status / decline_note coming back from /api/conversations.
  const conversationForThread = useMemo(() => {

    if (!thread || !conversations || !conversations.length) return null;

    const aliasStr = String(thread.alias || '').trim();
        const peerUidNum =
      thread.peerUid != null && thread.peerUid !== ''
        ? Number(thread.peerUid)
        : null;
    if (!aliasStr) return null;

    const hit = conversations.find((conv) => {
      if (!conv) return false;

      const convAlias =
        conv.alias ||
        conv.conversation_alias ||
        conv.room_alias ||
        conv.roomId ||
        '';

      // Primary: if both sides have an alias, try to match on that
      if (aliasStr && String(convAlias).trim() === aliasStr) {
        return true;
      }

      // Fallback: match on peer UID vs other_user_id
      const peerId = Number(conv.other_user_id || 0);
      if (
        Number.isFinite(peerUidNum) &&
        Number.isFinite(peerId) &&
        peerId === peerUidNum
      ) {
        return true;
      }

      return false;
    });


    return hit || null;
  }, [thread, conversations]);

  const status =
    (conversationForThread && conversationForThread.status) ||
    thread?.status ||
    null;

  const conversationType =
    (conversationForThread &&
      (conversationForThread.conversation_type ||
        conversationForThread.type ||
        null)) ||
    thread?.conversation_type ||
    thread?.conversationType ||
    null;

  const declineDateLabel = formatFullDateTime(
    (conversationForThread && (
      conversationForThread.sort_date ||
      conversationForThread.last_message_at ||
      conversationForThread.last_invited_at ||
      conversationForThread.created_at
    )) || null
  );

  const isDeclinedByPeer =
    !!thread &&
    isOutgoingInvite &&
    (status === 'declined' || conversationType === 'declined_dm');

  // Incoming side: has the invitee already declined this invite?
  const isDeclinedByMe =
    !!thread &&
    isIncomingInvite &&
    status === 'declined';

  const declinedByMeDateLabel = isDeclinedByMe
    ? formatFullDateTime(
        (conversationForThread && (
          conversationForThread.declined_at ||
          conversationForThread.sort_date ||
          conversationForThread.created_at
        )) || null
      )
    : null;

  const cooldownDaysRemaining = useMemo(() => {
    if (!thread || !isOutgoingInvite || !isDeclinedByPeer) return 0;

    const src = conversationForThread || thread;

    // Use the decline date for cooldown calculation (not the invite date)
    const declinedAtIso =
      (src && (src.declined_at || src.declinedAt)) || null;

    // Fall back to invite dates only if declined_at is missing
    const cooldownBase = declinedAtIso ||
      (src &&
        (src.last_invited_at ||
          src.first_invited_at ||
          src.sort_date ||
          src.created_at)) ||
      null;

    return calculateCooldownDaysRemaining(cooldownBase, 0.006944);
  }, [thread, isOutgoingInvite, isDeclinedByPeer, conversationForThread]);

  const isCooldownActive = cooldownDaysRemaining > 0;

  const cooldownLabel = isCooldownActive
    ? `You can try again in ${cooldownDaysRemaining} day${
        cooldownDaysRemaining === 1 ? '' : 's'
      }.`
    : 'You can send a new chat request now.';

  // Try all reasonable keys in both the conversation row and the thread
  const declineNoteFromThread =
    (conversationForThread &&
      (conversationForThread.decline_note ??
        conversationForThread.declineNote ??
        conversationForThread.decline_message ??
        null)) ||
    (thread &&
      (thread.decline_note ??
        thread.declineNote ??
        thread.decline_message ??
        null)) ||
    null;


  const truncatedInvitePreview = useMemo(() => {
    if (!thread) return '';
    const base =
      thread.lastInviteMessage ||
      thread.lastMessagePreview ||
      '';
    const raw = String(base || '').trim();
    if (!raw) return '';
    return raw.length > 240 ? raw.slice(0, 240) + '…' : raw;
  }, [thread]);

  // Matrix room + messages (if a real Matrix room is selected)
  const room = useMemo(() => {
    if (!selectedRoomId) return null;

    let r = typeof getRoom === 'function' ? getRoom(selectedRoomId) : null;

    // Fallback: ask the live Matrix client directly
    if (!r && mxClient && typeof mxClient.getRoom === 'function') {
      r = mxClient.getRoom(selectedRoomId) || null;
    }

    return r || null;
  }, [selectedRoomId, getRoom, mxClient]);

  // Header identity for an active Matrix DM (when there is no invite thread)
  const dmHeader = useMemo(() => {
    if (!selectedRoomId) return null;

    const selStr = String(selectedRoomId);

    // If conversations has the active Matrix room row (DM or team), use it
    const conv =
      conversations &&
      conversations.find(
        (c) =>
          c &&
          (c.conversation_type === 'active_matrix_dm' || c.conversation_type === 'team_room') &&
          c.room_id &&
          String(c.room_id) === selStr
      );

    if (conv) {
      const isTeam = conv.conversation_type === 'team_room';

      return {
        peerUid: isTeam
          ? null
          : (conv.other_user_id != null ? Number(conv.other_user_id) : null),
        title: conv.other_user_display_name || '',
        avatarUrl: isTeam ? null : (conv.other_user_avatar_url || null),
      };
    }


  // If thread identity exists (invite pending, retry, etc.), show that instead of blank.
  if (headerIdentity) {
    return {
      peerUid: headerIdentity.peerUid ?? null,
      title: headerIdentity.title,
      avatarUrl: headerIdentity.avatarUrl || null,
    };
  }

  return null; // No fallbacks — no 'Chat'
}, [selectedRoomId, conversations, headerIdentity]);



const effectiveHeaderTitle = headerIdentity?.title || dmHeader?.title || acceptedPeerIdentity?.title || '';
const effectiveHeaderAvatar =
  headerIdentity?.avatarUrl != null
    ? headerIdentity.avatarUrl
    : dmHeader?.avatarUrl ?? acceptedPeerIdentity?.avatarUrl ?? null;

const effectivePeerUid = headerIdentity?.peerUid ?? dmHeader?.peerUid ?? acceptedPeerIdentity?.peerUid ?? null;


// Selected conversation is a team room if the active conversations row says so.
// (No guessing: we rely on conversation_type === 'team_room' for the selected room_id.)
const isTeamRoom = useMemo(() => {
  if (!selectedRoomId) return false;

  const selStr = String(selectedRoomId);
  const conv =
    conversations &&
    conversations.find(
      (c) =>
        c &&
        c.conversation_type === 'team_room' &&
        c.room_id &&
        String(c.room_id) === selStr
    );

  return !!conv;
}, [selectedRoomId, conversations]);

const activeTeamId = useMemo(() => {
  if (!selectedRoomId || !conversations) return null;

  const selStr = String(selectedRoomId);
  const conv = conversations.find(
    (c) =>
      c &&
      c.conversation_type === 'team_room' &&
      c.room_id &&
      String(c.room_id) === selStr
  );

  // Preferred: backend-provided team_id (if/when available)
  const directId = Number(conv?.team_id);
  if (Number.isFinite(directId)) return directId;

  // Fallback: match the team room display name to a hydrated team in Zustand
  const convName = String(
    conv?.team_name || conv?.other_user_display_name || ''
  )
    .trim()
    .toLowerCase();

  if (!convName) return null;

  const match = (myTeams || []).find(
    (t) => String(t?.team_name || '').trim().toLowerCase() === convName
  );

  const matchedId = Number(match?.team_id);
  return Number.isFinite(matchedId) ? matchedId : null;
}, [selectedRoomId, conversations, myTeams]);

  // Ensure the currently signed-in user is actually JOINED to the selected team room.
  // This protects the captain side: the DB can show the room, but Matrix may still have them in INVITE state.
  useEffect(() => {
    const ensureTeamRoomJoin = async () => {
      if (!isTeamRoom) return;
      if (!selectedRoomId) return;
      if (!mxClient || typeof mxClient.getRoom !== 'function') return;
      if (typeof mxClient.joinRoom !== 'function') return;

      try {
        const room = mxClient.getRoom(selectedRoomId);
        const myMxid = typeof mxClient.getUserId === 'function' ? mxClient.getUserId() : null;

        // Prefer SDK helpers when available
        const membership =
          (typeof room?.getMyMembership === 'function' ? room.getMyMembership() : null) ||
          (myMxid && typeof room?.getMember === 'function' ? room.getMember(myMxid)?.membership : null) ||
          null;

        // If we are invited (or missing membership info), attempt a join once on selection.
        if (membership && String(membership).toLowerCase() === 'invite') {

          await mxClient.joinRoom(selectedRoomId);

        }
      } catch (err) {
        console.error('[ChatWorld] ensureTeamRoomJoin failed:', err);
      }
    };

    ensureTeamRoomJoin();
  }, [isTeamRoom, selectedRoomId, mxClient]);

  // Build roomMembers array for @mention autocomplete (team rooms only).
  // Fetches member list from our DB (team_memberships) — the Matrix SDK's
  // local sync misses members whose join events fall outside initialSyncLimit.
  const [roomMembers, setRoomMembers] = useState([]);
  useEffect(() => {
    if (!isTeamRoom || !activeTeamId) { setRoomMembers([]); return; }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/teams/members/ids?team_id=${activeTeamId}`, { credentials: 'include' });
        const data = await res.json();
        if (!data.ok || !Array.isArray(data.u_ids)) { if (!cancelled) setRoomMembers([]); return; }

        const otherIds = data.u_ids.filter((id) => id !== viewerUId);
        if (otherIds.length > 0) await fetchOtherUsersByIds(otherIds);

        const members = otherIds.map((uid) => {
          const storeUser = getOtherUser(uid);
          const displayName = storeUser?.displayName || `User ${uid}`;
          return {
            userId: uid,
            mxid: `@u${uid}:ehandshake.com`,
            displayName,
            avatarUrl: storeUser?.avatar || null,
          };
        });

        if (!cancelled) setRoomMembers(members);
      } catch (err) {
        console.error('[ChatWorld] roomMembers fetch failed:', err);
        if (!cancelled) setRoomMembers([]);
      }
    })();

    return () => { cancelled = true; };
  }, [isTeamRoom, activeTeamId, viewerUId, getOtherUser, fetchOtherUsersByIds]);

  // Extract just member names for MessageBubble @mention highlighting
  const roomMemberNames = useMemo(() => {
    return roomMembers.map((m) => m.displayName).filter(Boolean);
  }, [roomMembers]);

  // Build a display-name → userId mapping for clickable @mentions
  const roomMemberMap = useMemo(() => {
    const map = new Map();
    roomMembers.forEach((m) => {
      if (m.displayName && m.userId) map.set(m.displayName, m.userId);
    });
    return map;
  }, [roomMembers]);

  // Read the whole messages map from the store once,
  // then derive msgs based on selectedRoomId in React.
  const allMessages = useMatrixStore(state => state.messages);

  const msgs = useMemo(() => {
    if (!selectedRoomId) return [];
    return allMessages[selectedRoomId] || [];
  }, [allMessages, selectedRoomId]);

  // Determine the last message I sent that the other person has "seen" (DMs only)
  const lastSeenMessageId = useMemo(() => {
    if (!selectedRoomId || isTeamRoom || !msgs.length) return null;
    const roomReceipts = remoteReadReceipts[selectedRoomId];
    if (!roomReceipts) return null;

    const myMxid = mxClient?.getUserId?.() || null;
    if (!myMxid) return null;
    const myUid = Number(window.localStorage.getItem('u_id'));

    // In a DM, find the other user's latest receipt
    let receipt = null;
    for (const [userId, data] of Object.entries(roomReceipts)) {
      if (userId !== myMxid) { receipt = data; break; }
    }
    if (!receipt) return null;

    // Find receipt's position in message list
    const receiptIdx = msgs.findIndex((m) => m.eventId === receipt.eventId);

    if (receiptIdx >= 0) {
      // Walk backwards from receipt position to find last of MY messages
      for (let i = receiptIdx; i >= 0; i--) {
        const m = msgs[i];
        const mine =
          (m.senderUserId != null && Number(m.senderUserId) === myUid) ||
          m.sender === myMxid;
        if (mine) return m.id;
      }
    } else if (receipt.ts) {
      // Fallback: receipt eventId not in loaded messages — use timestamp
      for (let i = msgs.length - 1; i >= 0; i--) {
        const m = msgs[i];
        const msgTs = m.timestamp || m.ts || m.origin_server_ts || 0;
        if (msgTs > receipt.ts) continue;
        const mine =
          (m.senderUserId != null && Number(m.senderUserId) === myUid) ||
          m.sender === myMxid;
        if (mine) return m.id;
      }
    }
    return null;
  }, [msgs, selectedRoomId, isTeamRoom, remoteReadReceipts, mxClient]);

  // Keep the Matrix timeline scrolled to the bottom whenever messages change
  const messagesContainerRef = useRef(null);
  const prevRoomIdRef = useRef(null);
  const skipPaginationRef = useRef(false);

  // Debounced read-receipt sender — avoids spamming the homeserver on rapid room switches
  const receiptTimerRef = useRef(null);
  const sendReceiptDebounced = useCallback((roomId) => {
    if (receiptTimerRef.current) clearTimeout(receiptTimerRef.current);
    receiptTimerRef.current = setTimeout(() => {
      matrixUserService.sendReadReceipt(roomId);
    }, 500);
  }, []);

  // Clean up pending receipt timer on unmount
  useEffect(() => {
    return () => {
      if (receiptTimerRef.current) clearTimeout(receiptTimerRef.current);
    };
  }, []);

  // Pagination state for the selected room
  const paginationState = useMatrixStore(
    (s) => s.paginationState[s.selectedRoomId] ?? DEFAULT_PAGINATION
  );

  // Scroll-to-top handler: triggers loading older messages
  const handlePaginationScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el || !selectedRoomId) return;
    if (skipPaginationRef.current) return;
    if (!paginationState.hasMore || paginationState.isLoading) return;

    // Trigger load when within 100px of the top
    if (el.scrollTop < 100) {
      skipPaginationRef.current = true;
      const prevScrollHeight = el.scrollHeight;
      matrixUserService.loadOlderMessages(selectedRoomId, 30).then(() => {
        // Preserve scroll position after prepending older messages
        requestAnimationFrame(() => {
          const newEl = messagesContainerRef.current;
          if (newEl) {
            newEl.scrollTop = newEl.scrollHeight - prevScrollHeight;
          }
          // Re-enable after position is restored
          requestAnimationFrame(() => { skipPaginationRef.current = false; });
        });
      });
    }
  }, [selectedRoomId, paginationState.hasMore, paginationState.isLoading]);

  // Track scroll position for "scroll to bottom" button
  const handleScrollTracking = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollDown(distFromBottom > 300);
  }, []);

  // Attach scroll listeners for pagination + scroll-down tracking
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      handlePaginationScroll();
      handleScrollTracking();
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [handlePaginationScroll, handleScrollTracking]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;

    if (prevRoomIdRef.current !== selectedRoomId) {
      // Room changed — suppress pagination during the transition snap
      skipPaginationRef.current = true;
      prevRoomIdRef.current = selectedRoomId;

      // Wait for DOM to flush new messages, then snap to bottom
      requestAnimationFrame(() => {
        const freshEl = messagesContainerRef.current;
        if (freshEl) freshEl.scrollTop = freshEl.scrollHeight;
        // Re-enable pagination after the snap settles
        requestAnimationFrame(() => { skipPaginationRef.current = false; });
      });
    } else {
      // Same room, new message — only snap if already near the bottom
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
      if (isNearBottom) {
        // Suppress pagination during programmatic snap so the handler
        // doesn't see scrollTop < 100 mid-transition and trigger a load loop.
        skipPaginationRef.current = true;
        el.scrollTop = el.scrollHeight;
        requestAnimationFrame(() => { skipPaginationRef.current = false; });
      }
    }

    // Send a read receipt when the user is viewing this room
    if (selectedRoomId && msgs.length > 0) {
      sendReceiptDebounced(selectedRoomId);
    }
  }, [selectedRoomId, msgs.length, sendReceiptDebounced]);


  // Send a plain text message (or save an edit) into the currently selected Matrix room
  const handleSendMessage = async () => {
    if (!mxClient && !mxService) return;
    if (!selectedRoomId) return;
    if (isSendingMessage) return;

    const trimmed = (composerText || '').trim();
    if (!trimmed) return;

    // ===== EDIT MODE: save the edit instead of sending a new message =====
    if (editingMessage && editingMessage.eventId) {
      const editCtx = editingMessage;
      setComposerText('');
      setEditingMessage(null);
      setIsSendingMessage(true);

      try {
        await matrixUserService.editMessage(selectedRoomId, editCtx.eventId, trimmed);
      } catch (err) {
        console.error('[ChatWorld] Edit message failed:', err);
      }

      setIsSendingMessage(false);
      return;
    }

    // ===== NORMAL SEND =====
    // Capture reply context before clearing state
    const replyCtx = replyingTo;

    // Clear input IMMEDIATELY for snappy UX (don't wait for server round-trip)
    // Matrix SDK's local echo will show the message optimistically
    setComposerText('');
    setReplyingTo(null);
    setIsSendingMessage(true);

    try {
      if (replyCtx && replyCtx.eventId) {
        // Send as a reply
        await matrixUserService.sendReply(
          selectedRoomId,
          replyCtx.eventId,
          replyCtx.sender || '',
          replyCtx.body || '',
          trimmed
        );
      } else if (mxService && typeof mxService.sendMessage === 'function') {
        await mxService.sendMessage(selectedRoomId, trimmed);
      } else if (mxClient && typeof mxClient.sendTextMessage === 'function') {
        await mxClient.sendTextMessage(selectedRoomId, trimmed);
      }
    } catch (err) {
      console.error('[ChatWorld] Send message failed:', err);
    }

    setIsSendingMessage(false);

    // Scroll to bottom after sending (even if user was scrolled up for a reply)
    requestAnimationFrame(() => {
      const el = messagesContainerRef.current;
      if (el) {
        skipPaginationRef.current = true;
        el.scrollTop = el.scrollHeight;
        requestAnimationFrame(() => { skipPaginationRef.current = false; });
      }
    });

    // Clear typing indicator immediately on send
    try { mxClient?.sendTyping?.(selectedRoomId, false); } catch {}
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  };

  // ===== Emoji reaction handlers =====
  const handleReact = useCallback(async (targetEventId, emoji) => {
    if (!selectedRoomId || !targetEventId || !emoji) return;
    try {
      await matrixUserService.sendReaction(selectedRoomId, targetEventId, emoji);
    } catch (err) {
      console.error('[ChatWorld] sendReaction failed:', err);
    }
  }, [selectedRoomId]);

  const handleRemoveReact = useCallback(async (reactionEventId) => {
    if (!selectedRoomId || !reactionEventId) return;
    try {
      await matrixUserService.removeReaction(selectedRoomId, reactionEventId);
    } catch (err) {
      console.error('[ChatWorld] removeReaction failed:', err);
    }
  }, [selectedRoomId]);

  // ===== Reply handler =====
  const handleReply = useCallback((message) => {
    if (!message || !message.eventId) return;
    const myMxid = mxClient?.getUserId?.() || null;
    const senderMxid = message.sender || '';
    let senderName = 'Message';
    if (senderMxid === myMxid) {
      senderName = 'You';
    } else if (mxClient) {
      const mxUser = mxClient.getUser?.(senderMxid);
      const mxName = mxUser?.displayName || '';
      if (mxName && !mxName.startsWith('@')) {
        senderName = mxName;
      } else {
        const uid = senderMxid.match(/^@u(\d+):/)?.[1];
        if (uid) {
          const store = getOtherUser(Number(uid));
          senderName = store?.displayName || senderMxid;
        } else {
          senderName = senderMxid;
        }
      }
    }
    setReplyingTo({
      eventId: message.eventId,
      sender: message.sender,
      senderName,
      body: message.body || '',
    });
  }, [mxClient, getOtherUser]);

  const handleCancelReply = useCallback(() => {
    setReplyingTo(null);
  }, []);

  // ===== Edit handler =====
  const handleEditMessage = useCallback((message) => {
    if (!message || !message.eventId) return;
    setEditingMessage(message);
    setReplyingTo(null); // can't reply + edit at the same time
    setComposerText(message.body || '');
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingMessage(null);
    setComposerText('');
  }, []);

  // ===== Delete handler =====
  const handleDeleteMessage = useCallback((message) => {
    if (!message || !message.eventId) return;
    setDeletingMessage(message);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingMessage || !deletingMessage.eventId || !selectedRoomId) return;
    try {
      await matrixUserService.deleteMessage(selectedRoomId, deletingMessage.eventId);
    } catch (err) {
      console.error('[ChatWorld] Delete message failed:', err);
    }
    setDeletingMessage(null);
  }, [deletingMessage, selectedRoomId]);

  const handleCancelDelete = useCallback(() => {
    setDeletingMessage(null);
  }, []);

  // ===== @Mention click → navigate to / create DM =====
  const handleMentionClick = useCallback(async (targetUserId) => {
    const targetUid = Number(targetUserId);
    if (!Number.isFinite(targetUid) || targetUid <= 0) return;
    if (targetUid === viewerUId) return; // ignore self-mention clicks

    try {
      // 1. Check if an existing DM room exists
      const existingConv = useConversationStore.getState().findDmByPeerUid(targetUid);

      if (existingConv && existingConv.room_id) {
        // Existing DM found — switch to it
        setSelectedThread(existingConv.room_id);
        useMatrixStore.getState().setSelectedRoomId(existingConv.room_id);
        return;
      }

      // 2. No existing DM — create one via the server
      const res = await fetch('/api/matrix/create-dm', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidateUserId: targetUid }),
      });

      const data = await res.json().catch(() => null);

      if (!data || !data.success || !data.roomId) {
        console.error('[ChatWorld] create-dm failed for @mention click:', data);
        return;
      }

      // 3. Refresh the conversations list so the new DM appears in the sidebar
      useConversationStore.getState().loadConversations().catch(() => {});

      // 4. Add a local welcome system message
      useMatrixStore.getState().addMessage(data.roomId, {
        body: 'Welcome to your new DM room',
        system: true,
        timestamp: Date.now(),
        isMine: false,
      });

      // 5. Switch to the new DM room
      setSelectedThread(data.roomId);
      useMatrixStore.getState().setSelectedRoomId(data.roomId);

    } catch (err) {
      console.error('[ChatWorld] handleMentionClick error:', err);
    }
  }, [viewerUId, setSelectedThread]);

  // ===== Scroll to bottom =====
  const scrollToBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    skipPaginationRef.current = true;
    el.scrollTop = el.scrollHeight;
    setShowScrollDown(false);
    requestAnimationFrame(() => { skipPaginationRef.current = false; });
  }, []);

  // ===== Scroll to a specific message (for clicking reply quotes) =====
  const handleScrollToMessage = useCallback((eventId) => {
    if (!eventId || !messagesContainerRef.current) return;
    const el = messagesContainerRef.current.querySelector(`[data-event-id="${eventId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Brief highlight flash using inline style (Tailwind classes added via JS don't work)
      el.style.backgroundColor = 'rgba(0, 174, 247, 0.12)';
      el.style.borderRadius = '12px';
      el.style.transition = 'background-color 1.5s ease';
      setTimeout(() => { el.style.backgroundColor = ''; el.style.borderRadius = ''; }, 1500);
    }
  }, []);

  // ===== Typing indicator broadcast =====
  const handleComposerTyping = useCallback(() => {
    if (!mxClient || !selectedRoomId) return;

    // Tell the server we're typing (4s server-side timeout as safety net)
    try { mxClient.sendTyping(selectedRoomId, true, 4000); } catch {}

    // Reset the "stop typing" timer — fires 3s after last keystroke
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      try { mxClient.sendTyping(selectedRoomId, false); } catch {}
      typingTimeoutRef.current = null;
    }, 3000);
  }, [mxClient, selectedRoomId]);

  // Clean up typing timer, reply state, and edit state on room change or unmount
  useEffect(() => {
    setReplyingTo(null);
    setEditingMessage(null);
    setDeletingMessage(null);
    setComposerText('');
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [selectedRoomId]);

  // Send a Team Invite message into the currently selected Matrix room
  // (Phase B: captain-side only)
  const handleSendTeamInvite = async () => {
    if (!mxClient && !mxService) return;
    if (!selectedRoomId) return;

    setIsInviteReadyToastOpen(false);

    const teamId = Number(inviteDraft?.team_id);
    const inviteeId = Number(inviteDraft?.invitee_u_id);

    if (!Number.isFinite(teamId) || !Number.isFinite(inviteeId)) return;


    const inviterId = Number(window.localStorage.getItem('u_id'));
    if (!Number.isFinite(inviterId)) {
      console.error('[ChatWorld] Cannot send team invite: missing inviter u_id in localStorage');
      alert('Unable to send invite: missing inviter identity. Please refresh and try again.');
      return;
    }

    try {
      // 1) Create DB invite first (source of truth)
      const res = await fetch('/api/teams/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          team_id: teamId,
          u_id_inviter: inviterId,
          invitee_u_id: inviteeId,
        }),
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        console.error('[ChatWorld] /api/teams/invite HTTP error:', res.status, errText);

        if (res.status === 409) {
          alert('A pending invite already exists for this user on this team.');
          return;
        }

        alert('There was a problem creating the team invite. Please try again.');
        return;
      }

      const data = await res.json().catch(() => null);
      if (!data || !data.ok || !data.invite || !data.invite.invite_token) {
        console.error('[ChatWorld] /api/teams/invite bad payload:', data);
        alert('There was a problem creating the team invite. Please try again.');
        return;
      }

      const invite_token = data.invite.invite_token;
      const expires_at = data.invite.expires_at || null;
      const team_name_from_api = data.team_name || null;

      // 2) Send Matrix event as delivery mechanism (embed DB token)
      const payload = {
        type: 'eh.team_invite',
        team_id: teamId,
        team_name: team_name_from_api, // authoritative team display name
        inviter_u_id: inviterId,
        invitee_u_id: inviteeId,
        invite_token,
        expires_at,
        created_at: Date.now(),
      };

      const body = `__EH_TEAM_INVITE__::${JSON.stringify(payload)}`;

      if (mxService && typeof mxService.sendMessage === 'function') {
        await mxService.sendMessage(selectedRoomId, body);
      } else if (mxClient && typeof mxClient.sendTextMessage === 'function') {
        await mxClient.sendTextMessage(selectedRoomId, body);
      }



      // Clear the captain-side draft so the Send button disappears
      setInviteDraft(null);
    } catch (err) {
      console.error('[ChatWorld] Failed to create/send team invite', err);
      alert('There was a problem sending your invite. Please try again.');
    }
  };






  const setSelectedRoomId = useMatrixStore((s) => s.setSelectedRoomId);


  const handleCancelInvite = () => {
    if (!thread) return;

    try {
      if (retryMode) {
        // RETRY MODE: put this conversation back into archive "like it was"
        if (thread.alias) {
          archiveThread(thread.alias);
        }
        setRetryMode(false);
        setSelectedThread(null);
        return;
      }

      // FIRST-TIME INVITE MODE: remove the optimistic DM thread entirely
      if (thread.alias) {
        removeOptimisticDM(thread.alias);
      }

      if (thread.peerUid) {
        removePendingThread(thread.peerUid);
      }

      setSelectedThread(null);
    } catch (e) {
      console.error('[ChatWorld] Failed to cancel local invite thread', e);
    }
  };

  const handleRetryInvite = () => {
    if (!thread) return;
    setRetryMode(true);
    setInviteMessage('');
  };

  const handleSendInvite = async () => {
    const wasRetry = retryMode;
    if (!thread || !thread.peerUid) {
      console.error(
        '[ChatWorld] Cannot send invite: missing optimistic thread or peerUid',
        { thread }
      );
      return;
    }

    // Guard against double-clicks / re-sends from this view
    if (inviteSent || isSendingInvite) {

      return;
    }

    try {
      setIsSendingInvite(true);

      const payload = {
        invitee_u_id: thread.peerUid,
        // If the input is empty/whitespace, send null;
        // backend will store "Pending Chat Request" as the fallback.
        initial_message:
          inviteMessage && inviteMessage.trim().length > 0
            ? inviteMessage
            : null,
      };



      const token = localStorage.getItem('token');
      const res = await fetch('/api/dm/ensure', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => null);

        // Self-block: user previously blocked this person
        if (res.status === 403 && errData && errData.blocked_by_self) {
          setSelfBlockModal({
            open: true,
            blockedAt: errData.blocked_at || null,
            otherUserId: payload.invitee_u_id,
            otherUserName: headerTitle || 'this user',
          });
          return;
        }

        console.error(
          '[ChatWorld] /api/dm/ensure HTTP error:',
          res.status,
          errData
        );
        alert('There was a problem sending your invite. Please try again.');
        return;
      }

      const data = await res.json().catch(() => null);

      if (!data || !data.ok) {
        console.error('[ChatWorld] /api/dm/ensure failed payload:', data);
        alert('There was a problem sending your invite. Please try again.');
        return;
      }



      // Persistently mark the optimistic thread as "sent" and keep focus on it
      if (thread.alias) {
        markInviteSent(thread.alias);
        setSelectedThread(thread.alias);
      }

      // Clear the textarea in this view
      setInviteMessage('');

      // Leave retry mode so the success UI shows again
      setRetryMode(false);
      if (wasRetry) {
        setSuppressDeclinedView(true);
      }


    } catch (err) {
      console.error('[ChatWorld] Error sending DM invite:', err);
      alert('There was a problem sending your invite. Please try again.');
    } finally {
      setIsSendingInvite(false);
    }
  };

  // --- Incoming invitee handlers (ported from ChatView.jsx) ------------------

  const handleAcceptInvite = async () => {
    if (!thread || !thread.dmId) {
      console.error('[ChatWorld] Cannot accept invite: missing dmId', {
        thread,
      });
      alert('Unable to accept this invite right now. Please try again.');
      return;
    }

    const dmId = thread.dmId;
    const peerUid = thread.peerUid;

    // Capture header identity BEFORE any state changes trigger re-renders
    // (once state changes fire, headerIdentity may become null)
    const savedIdentity = headerIdentity ? { ...headerIdentity } : null;
    if (savedIdentity) setAcceptedPeerIdentity(savedIdentity);

    try {
      setIsAcceptingInvite(true);
      setHasAcceptedInvite(true);

      const acceptPayload = { dm_id: dmId };



      const acceptToken = localStorage.getItem('token');
      const res = await fetch('/api/dm/accept', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(acceptToken && { Authorization: `Bearer ${acceptToken}` }),
        },
        body: JSON.stringify(acceptPayload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error(
          '[ChatWorld] /api/dm/accept HTTP error:',
          res.status,
          text
        );
        alert('There was a problem accepting this invite. Please try again.');
        setHasAcceptedInvite(false);
        return;
      }

      const data = await res.json().catch(() => null);

      if (!data || !data.ok || !data.dm) {
        console.error('[ChatWorld] /api/dm/accept failed payload:', data);
        alert('There was a problem accepting this invite. Please try again.');
        setHasAcceptedInvite(false);
        return;
      }



      // Update optimistic thread so the list shows "connected"
      if (thread.alias) {
        const aliasKey = String(thread.alias);
        useMessagingStore.setState((state) => ({
          optimisticThreads: (state.optimisticThreads || []).map((t) => {
            if (!t) return t;
            if (String(t.alias) !== aliasKey) return t;
            return {
              ...t,
              status: 'accepted',
              lastMessagePreview: 'You are now connected.',
              lastMessageAt: Date.now(),
            };
          }),
        }));
      }

      // Immediately refresh conversation list so the UI reflects the acceptance
      useConversationStore.getState().loadConversations().catch(() => {});

      // Kick off Matrix DM room provisioning
      if (Number.isFinite(Number(peerUid))) {
        const rawMyId = window.localStorage.getItem('u_id');
        const myUserId =
          rawMyId != null && rawMyId !== '' ? Number(rawMyId) : NaN;

        const matrixPayload = {
          candidateUserId: Number(peerUid),
          myUserId: Number.isFinite(myUserId) ? myUserId : null,
          dmId,
        };

        fetch('/api/matrix/create-dm', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(matrixPayload),
        })
          .then((r) => r.json().catch(() => null))
          .then((matrixData) => {


            if (matrixData && matrixData.success && matrixData.roomId) {

              // Switch selection to the real Matrix room
              setSelectedThread(matrixData.roomId);
              setSelectedRoomId(matrixData.roomId);

              const acceptedAt = new Date().toISOString();

              if (mxClient) {
                try {
                  const rawMyId2 = window.localStorage.getItem('u_id');
                  const myUserId2 =
                    rawMyId2 != null && rawMyId2 !== ''
                      ? Number(rawMyId2)
                      : NaN;

                  mxClient
                    .sendStateEvent(
                      matrixData.roomId,
                      'com.ehandshake.invite.accepted',
                      {
                        inviter: myUserId2,
                        invitee: peerUid,
                        acceptedAt,
                      },
                      ''
                    )
                    .catch((err) => {
                      console.error(
                        '[ChatWorld] Error sending invite.accepted state event:',
                        err
                      );
                    });
                } catch (err) {
                  console.error(
                    '[ChatWorld] Exception sending invite.accepted state event:',
                    err
                  );
                }
              }
            }
          })
          .catch((err) => {
            console.error(
              '[ChatWorld] /api/matrix/create-dm request failed:',
              err
            );
          });
      }
    } catch (err) {
      console.error('[ChatWorld] Error accepting DM invite:', err);
      alert('There was a problem accepting this invite. Please try again.');
      setHasAcceptedInvite(false);
    } finally {
      setIsAcceptingInvite(false);
    }
  };

  const handleDeclineInvite = async () => {
    if (!thread || !thread.dmId) {
      console.error('[ChatWorld] Cannot decline invite: missing dmId', {
        thread,
      });
      alert('Unable to decline this invite right now. Please try again.');
      return;
    }

    const dmId = thread.dmId;

    try {
      const payload = {
        dm_id: dmId,
        // Optional note from the invitee; send null if blank
        decline_note:
          declineNote && declineNote.trim().length > 0
            ? declineNote.trim()
            : null,
      };



      const declineToken = localStorage.getItem('token');
      const res = await fetch('/api/dm/decline', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(declineToken && { Authorization: `Bearer ${declineToken}` }),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error(
          '[ChatWorld] /api/dm/decline HTTP error:',
          res.status,
          text
        );
        alert('There was a problem declining this invite. Please try again.');
        return;
      }

      const data = await res.json().catch(() => null);

      if (!data || !data.ok || !data.dm) {
        console.error(
          '[ChatWorld] /api/dm/decline failed payload:',
          data
        );
        alert('There was a problem declining this invite. Please try again.');
        return;
      }



      if (thread.alias) {
        archiveThread(thread.alias);
      }

      setDeclineNote('');
      setSelectedThread(null);

      // Immediately refresh conversation list so the declined invite disappears
      useConversationStore.getState().loadConversations().catch(() => {});
    } catch (err) {
      console.error('[ChatWorld] Error declining DM invite:', err);
      alert('There was a problem declining this invite. Please try again.');
    }
  };

  const handleBlockUser = async () => {
    if (!thread || !thread.dmId) {
      console.error('[ChatWorld] Cannot block: missing dmId', { thread });
      alert('Unable to block this user right now. Please try again.');
      return;
    }

    try {
      const blockToken = localStorage.getItem('token');
      const res = await fetch('/api/dm/block', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(blockToken && { Authorization: `Bearer ${blockToken}` }),
        },
        body: JSON.stringify({ dm_id: thread.dmId }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error('[ChatWorld] /api/dm/block HTTP error:', res.status, text);
        alert('There was a problem blocking this user. Please try again.');
        return;
      }

      const data = await res.json().catch(() => null);
      if (!data || !data.ok) {
        console.error('[ChatWorld] /api/dm/block failed payload:', data);
        alert('There was a problem blocking this user. Please try again.');
        return;
      }

      // Remove from both optimistic and archived lists so the blocked
      // conversation disappears completely from the invitee's view.
      if (thread.alias) {
        const alias = String(thread.alias);
        useMessagingStore.setState((s) => ({
          optimisticThreads: (s.optimisticThreads || []).filter(
            (t) => String(t.alias) !== alias
          ),
          archivedThreads: (s.archivedThreads || []).filter(
            (t) => String(t.alias) !== alias
          ),
        }));
      }

      setSelectedThread(null);
      useConversationStore.getState().loadConversations().catch(() => {});
    } catch (err) {
      console.error('[ChatWorld] Error blocking user:', err);
      alert('There was a problem blocking this user. Please try again.');
    }
  };

  const handleUnblock = async () => {
    const { otherUserId } = selfBlockModal;
    setSelfBlockModal({ open: false, blockedAt: null, otherUserId: null, otherUserName: '' });

    try {
      const unblockToken = localStorage.getItem('token');
      const res = await fetch('/api/dm/unblock', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(unblockToken && { Authorization: `Bearer ${unblockToken}` }),
        },
        body: JSON.stringify({ other_user_id: otherUserId }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error('[ChatWorld] /api/dm/unblock error:', res.status, text);
        alert('There was a problem unblocking this user. Please try again.');
        return;
      }

      // Refresh conversations and let the user re-send if they want
      useConversationStore.getState().loadConversations().catch(() => {});
    } catch (err) {
      console.error('[ChatWorld] Error unblocking:', err);
      alert('There was a problem unblocking this user. Please try again.');
    }
  };

  return (
    <div className="h-full w-full flex flex-1">
    <TeamInviteStatusModal
      open={teamInviteStatusModal.open}
      message={teamInviteStatusModal.message}
      onClose={() => setTeamInviteStatusModal({ open: false, message: '' })}
    />

    {/* Self-block modal: user tries to invite someone they previously blocked */}
    {selfBlockModal.open && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-5">
          <h3 className="text-base font-semibold text-gray-900 mb-2">
            User Blocked
          </h3>
          <p className="text-sm text-gray-700 mb-1">
            You blocked <strong>{selfBlockModal.otherUserName}</strong>
            {selfBlockModal.blockedAt && (
              <> on {new Date(selfBlockModal.blockedAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })} at {new Date(selfBlockModal.blockedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</>
            )}.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Would you like to unblock this user so you can send them a chat invitation?
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-4 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
              onClick={() => setSelfBlockModal({ open: false, blockedAt: null, otherUserId: null, otherUserName: '' })}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-1.5 rounded-md bg-[#00aef7] text-white hover:bg-[#009ad9] text-sm"
              onClick={handleUnblock}
            >
              Unblock
            </button>
          </div>
        </div>
      </div>
    )}

      {/* Left pane: existing chat list */}
      <div
        className="h-full border-r border-gray-200 bg-white"
        style={{ width: sidebarWidth }}
      >
        <ChatList />
      </div>

      {/* Vertical drag divider */}
      <VerticalDragDivider />

      {/* Right pane */}
      <div className="flex-1 h-full bg-white">
      {/* Header */}
      <div className="h-16 px-4 flex items-center border-b border-gray-200">
        {(thread || dmHeader || acceptedPeerIdentity) && (
          <>
            <PresenceAvatar
              src={effectiveHeaderAvatar}
              userId={effectivePeerUid}
              size={36}
              fallback={initialsOf(effectiveHeaderTitle)}
              className="mr-3"
              isTeam={isTeamRoom}
            />

              <div className="flex flex-col">

                {isTeamRoom && Number.isFinite(Number(activeTeamId)) ? (
                  <button
                    type="button"
                    onClick={() => openTeam(Number(activeTeamId))}
                    className="text-sm font-semibold text-gray-900 hover:underline text-left"
                    title="Open team details"
                  >
                    {effectiveHeaderTitle}
                  </button>
                ) : Number.isFinite(Number(effectivePeerUid)) ? (
                  <button
                    type="button"
                    onClick={() => openCandidateForUid(Number(effectivePeerUid))}  // ← This line
                    className="text-sm font-semibold text-gray-900 hover:underline text-left"
                    title="Open user details"
                  >
                    {effectiveHeaderTitle}
                  </button>
                ) : (
                  <div className="text-sm font-semibold text-gray-900">
                    {effectiveHeaderTitle}
                  </div>
                )}
              </div>


              {/* Invite-to-team icon (Captain action) — TEMP wiring */}
              {!isTeamRoom && (
                <button
                  type="button"
                  disabled={!canInviteToTeam}
                  onClick={() => {
                    if (!canInviteToTeam) return;

                    const peerUid = thread?.peerUid ?? dmHeader?.peerUid ?? null;

                    if (!peerUid) {

                      return;
                    }

                    setInviteDraft({
                      invitee_u_id: Number(peerUid),
                      team_id: null,
                      created_at: Date.now(),
                      status: 'draft',
                    });



                    // Phase A: open captain-only team picker (always, even for 1 team)
                    const list = Array.isArray(captainTeams) ? captainTeams : [];
                    const defaultId = Number(list[0]?.team_id);
                    setPickedTeamId(Number.isFinite(defaultId) ? defaultId : null);
                    setIsTeamPickerOpen(true);
                  }}
                  className={[
                    "ml-auto inline-flex items-center justify-center h-9 w-9 rounded-full",
                    canInviteToTeam
                      ? "hover:bg-[#00aef7]/10 text-[#00aef7]"
                      : "text-gray-300 cursor-not-allowed opacity-60",
                  ].join(" ")}
                  aria-label="Invite to a team"
                  aria-disabled={!canInviteToTeam}
                  title={canInviteToTeam ? "Invite to a team" : "Only team captains can invite"}
                >


                          {/* “User plus” icon (inline SVG) */}
                          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#00aef7"><path d="M720-400v-120H600v-80h120v-120h80v120h120v80H800v120h-80ZM247-527q-47-47-47-113t47-113q47-47 113-47t113 47q47 47 47 113t-47 113q-47 47-113 47t-113-47ZM40-160v-112q0-34 17.5-62.5T104-378q62-31 126-46.5T360-440q66 0 130 15.5T616-378q29 15 46.5 43.5T680-272v112H40Zm80-80h480v-32q0-11-5.5-20T580-306q-54-27-109-40.5T360-360q-56 0-111 13.5T140-306q-9 5-14.5 14t-5.5 20v32Zm296.5-343.5Q440-607 440-640t-23.5-56.5Q393-720 360-720t-56.5 23.5Q280-673 280-640t23.5 56.5Q327-560 360-560t56.5-23.5ZM360-640Zm0 400Z"/></svg>
                        </button>
                      )}
          {/* Send team invite (Phase B) — enabled only after team is selected */}
          {canInviteToTeam &&
            Number.isFinite(Number(inviteDraft?.team_id)) &&
            Number(inviteDraft?.invitee_u_id) === Number(effectivePeerUid) &&
            !!selectedRoomId && (
              <div className="relative ml-2">
                <button
                  type="button"
                  onClick={handleSendTeamInvite}
                  className="px-3 py-1.5 rounded-md bg-[#00aef7] text-white hover:bg-[#009ad9] text-sm"
                  title="Send team invitation"
                >
                  Send invite
                </button>

                  {isInviteReadyToastOpen && (
                    <div className="absolute right-0 top-full mt-2 z-50">
                      <div className="relative bg-green-50 border border-green-300 shadow-lg rounded-md px-3 py-2 text-sm text-green-900 w-[320px]">
                        <div className="absolute -top-2 right-6 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-green-300" />
                        <div className="absolute -top-[7px] right-6 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-green-50" />

                        <div className="font-semibold">
                          Your invitation to {effectiveHeaderTitle || 'this user'} is ready to send
                        </div>

                        <div className="text-green-900/80 mt-1">
                          for team{' '}

                        {(() => {
                          const list = Array.isArray(captainTeams) ? captainTeams : [];
                          const t = list.find((x) => Number(x?.team_id) === Number(inviteDraft?.team_id));
                          const name = t?.team_name || t?.teamName;
                          return name ? name : `Team #${Number(inviteDraft?.team_id)}`;
                        })()}
                        .
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

          </>
        )}
      </div>

{/* === Captain Team Picker (Phase A) === */}
<TeamInvitePicker
  isOpen={isTeamPickerOpen}
  captainTeams={captainTeams}
  pickedTeamId={pickedTeamId}
  setPickedTeamId={setPickedTeamId}
  onClose={closeTeamPicker}
  onContinue={(chosenId) => {
    const currentDraft = useTeamStore.getState().inviteDraft;
    setInviteDraft({
      ...(currentDraft || {}),
      team_id: chosenId,
    });
    setIsTeamPickerOpen(false);
    setIsInviteReadyToastOpen(true);
  }}
/>

        {/* Body: invite compose / sent UI for outgoing optimistic threads */}
        <div className="h-[calc(100%-4rem)] flex flex-col">
          {!thread && !selectedRoomId && (
            <div className="flex-1 min-h-0 overflow-hidden">
              <DiscoverPeoplePanel />
            </div>
          )}



          {thread && isOutgoingInvite && (
            <InviteFlowOutgoing
              isDeclinedView={!retryMode && isDeclinedByPeer && !suppressDeclinedView}
              declineDateLabel={declineDateLabel}
              declineNoteFromThread={declineNoteFromThread}
              headerTitle={headerTitle}
              cooldownLabel={cooldownLabel}
              isCooldownActive={isCooldownActive}
              onRetry={handleRetryInvite}
              inviteSent={inviteSent}
              retryMode={retryMode}
              inviteMessage={inviteMessage}
              onInviteMessageChange={setInviteMessage}
              onCancel={handleCancelInvite}
              onSend={handleSendInvite}
              isSending={isSendingInvite}
            />
          )}


          {thread && !isOutgoingInvite && (
            isIncomingInvite ? (
              <InviteFlowIncoming
                hasAccepted={hasAcceptedInvite}
                headerTitle={headerTitle}
                lastMessagePreview={thread.lastMessagePreview}
                declineNote={declineNote}
                onDeclineNoteChange={setDeclineNote}
                onAccept={handleAcceptInvite}
                onDecline={handleDeclineInvite}
                onBlock={handleBlockUser}
                isAccepting={isAcceptingInvite}
                isDeclined={isDeclinedByMe}
                declinedDateLabel={declinedByMeDateLabel ? `Declined on ${declinedByMeDateLabel}` : null}
              />
            ) : (
              !selectedRoomId && (
                <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                  Conversation details will appear here.
                </div>
              )
            )
          )}

          {/* Matrix DM view when a real Matrix room is selected */}
          {selectedRoomId && (
            <>
              {/* Messages timeline */}
              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto overscroll-contain p-6 bg-gray-50"
              >
                {!msgs || msgs.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                    No messages yet. Start the conversation!
                  </div>
                ) : (
                  <>
                    {paginationState.isLoading && (
                      <div className="flex justify-center py-3">
                        <span className="text-xs text-gray-400 animate-pulse">Loading older messages...</span>
                      </div>
                    )}
                    {!paginationState.hasMore && !paginationState.isLoading && msgs.length > 0 && (
                      <div className="text-center text-xs text-gray-300 py-3">
                        Beginning of conversation
                      </div>
                    )}
                    {msgs.map((m) => {
                      const myUid = Number(window.localStorage.getItem('u_id'));
                      const myMxid =
                        mxClient && typeof mxClient.getUserId === 'function'
                          ? mxClient.getUserId()
                          : null;
                      const isMine =
                        (m.senderUserId != null &&
                          Number(m.senderUserId) === Number(myUid)) ||
                        (m.sender === myMxid);

                      // Look up reactions for this message
                      const roomReactions = allReactions[selectedRoomId] || {};
                      const msgReactions = m.eventId ? (roomReactions[m.eventId] || null) : null;

                      // Look up the reply-to message if this is a reply
                      const replyToMsg = m.replyToEventId
                        ? msgs.find((msg) => msg.eventId === m.replyToEventId) || null
                        : null;

                      return (
                        <div key={m.id} data-event-id={m.eventId || ''}>
                        <MessageBubble
                          message={m}
                          isMine={isMine}
                          isTeamRoom={isTeamRoom}
                          isLastSeen={m.id === lastSeenMessageId}
                          reactions={msgReactions}
                          myMxid={myMxid}
                          onReact={(emoji) => handleReact(m.eventId, emoji)}
                          onRemoveReact={(reactionEventId) => handleRemoveReact(reactionEventId)}
                          onReply={handleReply}
                          onEdit={handleEditMessage}
                          onDelete={handleDeleteMessage}
                          replyToMessage={replyToMsg}
                          onScrollToMessage={handleScrollToMessage}
                          myTeams={myTeams}
                          mxClient={mxClient}
                          getOtherUser={getOtherUser}
                          openTeam={openTeam}
                          setTeamInviteStatusModal={setTeamInviteStatusModal}
                          roomMemberNames={isTeamRoom ? roomMemberNames : undefined}
                          roomMemberMap={isTeamRoom ? roomMemberMap : undefined}
                          onMentionClick={isTeamRoom ? handleMentionClick : undefined}
                        />
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              {/* Scroll to bottom button */}
              {showScrollDown && (
                <div className="flex justify-center -mt-5 mb-1 relative z-10">
                  <button
                    type="button"
                    onClick={scrollToBottom}
                    className="bg-white border border-gray-300 shadow-md rounded-full w-9 h-9 flex items-center justify-center hover:bg-gray-50 transition-colors text-gray-500 hover:text-[#00aef7]"
                    title="Scroll to bottom"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                </div>
              )}

              {/* Typing indicator */}
              {(() => {
                const myMxid = mxClient?.getUserId?.() || '';
                const roomTypers = (typingUsers[selectedRoomId] || []).filter(
                  (uid) => uid !== myMxid
                );
                if (!roomTypers.length) return null;

                const room = mxClient?.getRoom?.(selectedRoomId);
                const names = roomTypers.map((uid) => {
                  const member = room?.getMember?.(uid);
                  return member?.name || uid;
                });

                const label =
                  names.length === 1
                    ? `${names[0]} is typing`
                    : names.length === 2
                      ? `${names[0]} and ${names[1]} are typing`
                      : 'Several people are typing';

                return (
                  <div className="px-4 h-6 flex items-center text-xs text-gray-400 italic">
                    <span className="flex gap-0.5 mr-1.5">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                    {label}
                  </div>
                );
              })()}

              <MessageComposer
                value={composerText}
                onChange={setComposerText}
                onSend={handleSendMessage}
                onTyping={handleComposerTyping}
                disabled={isSendingMessage}
                replyingTo={replyingTo}
                onCancelReply={handleCancelReply}
                editingMessage={editingMessage}
                onCancelEdit={handleCancelEdit}
                isTeamRoom={isTeamRoom}
                roomMembers={isTeamRoom ? roomMembers : undefined}
              />
            </>
          )}

        </div>
      </div>

      {/* Delete confirmation dialog */}
      {deletingMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm mx-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Delete message?</h3>
            <p className="text-xs text-gray-500 mb-4">
              Are you sure? This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-1.5 text-xs rounded-lg bg-red-500 text-white hover:bg-red-600"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default ChatWorld;



